import {DataSourceType} from '@app/views/admin/application-settings/models/data-source-type.model';

export class OrgItem {
    oneRosterIdentifier: string;
    parentId?: string;

    constructor(
        public identifier: string = '',
        public name: string = '',
        public type: OrgItemType = null
    ) { }

    static compare(orgItemA: OrgItem, orgItemB: OrgItem, pathParser: OUPathParser): number {
        const numberOfNodesDiff = pathParser.diff(orgItemA.identifier, orgItemB.identifier);
        if (numberOfNodesDiff !== 0) {
            return numberOfNodesDiff;
        } else {
            if (orgItemA.type === orgItemB.type) {
                return (orgItemA.name || orgItemA.identifier || '').localeCompare(orgItemB.name || orgItemB.identifier || '');
            } else {
                if (orgItemA.type === OrgItemType.OU) {
                    return -1;
                } else if (orgItemA.type === OrgItemType.GROUP) {
                    return 1;
                } else {
                    // orgItemA.type === OrgItemType.CONTAINER
                    return (orgItemB.type === OrgItemType.GROUP) ? -1 : 1;
                }
            }
        }
    }

    static getOrgName(orgIdentifier: string, orgName: string, orgType: OrgItemType, dataSourceType: DataSourceType): string {
        if (dataSourceType === DataSourceType.LDAP) {
            return orgIdentifier;
        } else if (dataSourceType === DataSourceType.GSuite) {
            return ((orgType === OrgItemType.GROUP) || (orgIdentifier === GSUITE_ROOT_IDENTIFIER))
                ? orgName
                : orgIdentifier;
        } else {
            return orgIdentifier;
        }
    }
}

export enum OrgItemType {
    OU = 'orgunit',
    CONTAINER = 'container',
    GROUP = 'group'
}

export const DEFAULT_OU_LEVEL_SEPARATOR = ',';
export const LDAP_OU_LEVEL_SEPARATOR = ',';
export const GSUITE_OU_LEVEL_SEPARATOR = '/';
export const GSUITE_ROOT_IDENTIFIER = '/';
export const LDAP_OU_PREFIX = 'OU';
export const LDAP_GROUP_PREFIX = 'CN';

export class OUPathParser {
    constructor(
        public dataSourceType: DataSourceType = null
    ) { }

    public getNodeList(path: string, fullPath = true): string[] {
        if (this.dataSourceType === DataSourceType.LDAP) {
            return (path || '')
                .match(/\w([^\\,]|\\.)+/g)
                .reduceRight((result: string[], current: string) => {
                    if (current.toUpperCase().startsWith('DC=')) {
                        if (fullPath) {
                            result = (result.length === 0) ? [current] : [current + LDAP_OU_LEVEL_SEPARATOR + result[0]];
                        }
                    } else {
                        if ((result.length === 1) && (result[0].toUpperCase().startsWith('DC='))) {
                            result = [current + LDAP_OU_LEVEL_SEPARATOR + result[0]];
                        } else {
                            result.push(current);
                        }
                    }
                    return result;
                }, []);
        } else if (this.dataSourceType === DataSourceType.GSuite) {
            return (path || '').split(GSUITE_OU_LEVEL_SEPARATOR);
        } else {
            return [path || ''];
        }
    }

    public getNumberOfNodes(path: string): number {
        return this.getNodeList(path).length;
    }

    public getParentNodeList(path: string): string[] {
        // Use full DN for top OU - fullPath = true (false elsewhere)
        const nodes = this.getNodeList(path, true);
        nodes.pop();
        if (this.dataSourceType === DataSourceType.GSuite) {
            if (path.startsWith(GSUITE_OU_LEVEL_SEPARATOR) && (nodes.length > 0)) {
                // Set Root node name
                nodes[0] = GSUITE_OU_LEVEL_SEPARATOR;
            }
            // nodes.shift();
        }
        return nodes;
    }

    public getPath(nodeList: string[]): string {
        if (this.dataSourceType === DataSourceType.LDAP) {
            return [...nodeList].reverse().join(LDAP_OU_LEVEL_SEPARATOR);
        } else if (this.dataSourceType === DataSourceType.GSuite) {
            return nodeList.join(GSUITE_OU_LEVEL_SEPARATOR);
        } else {
            return nodeList.join(DEFAULT_OU_LEVEL_SEPARATOR);
        }
    }

    public isFirstPathPartMatched(path: string, pathPart: string): boolean {
        if (this.dataSourceType === DataSourceType.LDAP) {
            return path.toLocaleLowerCase().endsWith(pathPart.toLocaleLowerCase().trim());
        } else if (this.dataSourceType === DataSourceType.GSuite) {
            const pathPartLength = (pathPart === GSUITE_OU_LEVEL_SEPARATOR) ? 0 : pathPart.length;
            return (path.toLocaleLowerCase().indexOf(pathPart.toLocaleLowerCase().trim()) === 0)
                && ((path.length === pathPartLength) || (path[pathPartLength] === GSUITE_OU_LEVEL_SEPARATOR));
        } else {
            return (path.toLocaleLowerCase().indexOf(pathPart.toLocaleLowerCase().trim()) === 0)
                && ((path.length === pathPart.length) || (path[pathPart.length] === DEFAULT_OU_LEVEL_SEPARATOR));
        }
    }

    public isPathPartMatched(path: string, pathPart: string): boolean {
        return (path.toLocaleLowerCase().indexOf(pathPart.toLocaleLowerCase().trim()) >= 0);
    }

    public getOrgType(path: string): OrgItemType {
        if (this.dataSourceType === DataSourceType.LDAP) {
            const pos = path.indexOf('=');
            if (pos >= 0) {
                const ouType = path.substr(0, pos);
                if (ouType === LDAP_OU_PREFIX) {
                    return OrgItemType.OU;
                } else if (ouType === LDAP_GROUP_PREFIX) {
                    return OrgItemType.GROUP;
                }
            }

            return null;
        } else if (this.dataSourceType === DataSourceType.GSuite) {
            // ToDo: parse Group
            return OrgItemType.OU;
        } else {
            return OrgItemType.OU;
        }
    }

    public getLastNode(path: string): string {
        const nodes = this.getNodeList(path);
        return (nodes.length > 0) ? nodes[nodes.length - 1] : '';
    }

    public diff(pathA: string, pathB: string) {
        let nodeListPathA = this.getNodeList(pathA);
        let nodeListPathB = this.getNodeList(pathB);
        if (this.dataSourceType === DataSourceType.GSuite) {
            // Skip empty to correct sorting of Google Root (/) node
            nodeListPathA = nodeListPathA.filter((item: string) => !!item);
            nodeListPathB = nodeListPathB.filter((item: string) => !!item);
        }
        return nodeListPathA.length - nodeListPathB.length;
    }

    public getOrgName(org: OrgItem): string {
        return OrgItem.getOrgName(org.identifier, org.name, org.type, this.dataSourceType);
    }
}

export class OrgItemChanges {
    public newOrg: OrgItem;
    public isDisabled: boolean;
    public isExpanded: boolean;

    constructor(
        public org: OrgItem
    ) { }

    isDismissed(): boolean {
        return this.newOrg === null;
    }
}
